import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "components/base/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faFileExport, faAngleDown, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Dropzone from "components/custom/CustomDropzoneUpdate";
import TinymceEditor from "components/base/TinymceEditor";
import VariantFormCard from "components/cards/VariantFormCard";
import ProductVariantsTable from "components/custom/UpdateVariantsTable";
import { Col, Form, Row, Card, Toast, ToastContainer, Badge, Container, Modal } from "react-bootstrap";
import CustomCascader from "components/custom/CustomCascader";
import ProductTabs from "components/tabs/ProductTabs";
import client from "api/client";
import { CATEGORIES_QUERY } from "api/graphql/queries/categoriesQuery";
import productAttributes from "config/productAttributes";
import { Typeahead } from "react-bootstrap-typeahead";
import { FileAttachment } from "components/custom/types";
import api from "api/api";
import { GET_FEATURES_QUERY } from "api/graphql/queries/Getfeatures";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_PRODUCT_DETAILS } from "api/graphql/queries/product/GetProductsDetails";
import { UPDATE_PRODUCT } from "api/graphql/mutations/product/UpdateProduct";
import { faPenToSquare, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { fetchAttributeOptions } from "utils/fetchAttributeOptions";
interface Option {
  option_id: string;
  attribute_id?: string;
  value?: string;
  swatch?: string;
}
interface OptionsGetter {
  method: string;
  url: string;
  param: {
    key: string;
    value: string;
  };
  token: string;
  minlength: number;
  debounceTime: number;
}
interface Attribute {
  attribute_id: string;
  attribute_code: string;
  frontend_label: string;
  is_required: string;
  input_type: string;
  options_getter?: OptionsGetter;
  options?: Option[];
}
interface AttributeGroup {
  name: string;
  icon?: string;
  display?: string;
  attributes: Attribute[];
}
interface Category {
  id: string;
  name: string;
  level: number;
  include_in_menu?: number; // ✅ Ensure this is defined
  children?: Category[];
}
type ProductInput = {
  name: string;
  category: {
    level2: any;
    level3?: any;
    level4?: any;
  };
  brand?: string;
  model_name?: string;
  model_number?: string;
  seller_reference?: string;
  short_description: string;
  description: string;
  gallery?: { preview: any; roles: string[] }[];
  price: number;
  special_price?: {
    value: number | null;
    from_date?: string | null;
    end_date?: string | null;
  };
  Livraison_gratuite: number;
  politique_de_retour: number;
  warranty?: number;
  mode_expedition: { option_id: any }[];
  quantity?: number;
  weight: number;
  dimensions_de_l_outil_largeur1: number;
  dimensions_de_l_outil_hauteur1: number;
  longueur25: number;
  variantsData?: {
    quantity: number;
    price: number;
    attributes: { code: string; value: string }[];
    gallery?: { preview: any; roles: string[] };
  }[];
  selectedAttributes?: string[];
  attributes?: {
    code: string;
    value: string | number;
  }[];
};
interface CategorySelection {
  level2: { id: string; parent_id: string; name: string } | null;
  level3: { id: string; parent_id: string; name: string } | null;
  level4: { id: string; parent_id: string; name: string } | null;
}
interface VariantAttribute {
  code: string;
  value: string;
  content?: string;
  label?: string;
}
interface Variant {
  id: string;
  sku: string;
  quantity: number;
  price: number;
  image?: {
    label: string;
    url: string;
    roles?: string[];
  };
  attributes: VariantAttribute[];
}

const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function (this: void, ...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
const fetchOptions = async (optionsGetter: OptionsGetter, query: string) => {
  if (query.length < optionsGetter.minlength) {
    return [];
  }
  try {
    const response = await api.get(
      `${optionsGetter.url}?${optionsGetter.param.key}=${query}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching options:", error);
    return [];
  }
};
const LOCAL_STORAGE_KEY = "addProductFormValues";
const ProductDetails: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate(); // Add navigate hook
  const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [isReadOnly, setIsReadOnly] = useState(true); // Track edit mode
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [originalValues, setOriginalValues] = useState<{ [key: string]: any }>({});

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<{ [key: string]: any }>(() => {
    const savedValues = localStorage.getItem(LOCAL_STORAGE_KEY);
    return savedValues ? JSON.parse(savedValues) : {};
  });
  const [characteristicsAttributes, setCharacteristicsAttributes] = useState<
    Attribute[]
  >([]);
  const [dynamicOptions, setDynamicOptions] = useState<{
    [key: string]: Option[];
  }>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
  //variations states
  const [variationAttributes, setVariationAttributes] = useState<Attribute[]>(
    []
  );
  const [isLoadingAttributes, setIsLoadingAttributes] =
    useState<boolean>(false);
  const [generatedVariants, setGeneratedVariants] = useState<any[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Attribute[]>([]);
  const [brandOptions, setBrandOptions] = useState<Option[]>([]);
  
  // Modal state for deactivation reason details
  const [showModal, setShowModal] = useState(false);
  const [currentReasonDetails, setCurrentReasonDetails] = useState<{ reason: string[]; description: string } | null>(null);

  // Handler for showing deactivation reason details in modal
  const handleShowReasonDetails = () => {
    if (data?.sellerProductDetails?.deactivation_reason) {
      setCurrentReasonDetails(data.sellerProductDetails.deactivation_reason);
      setShowModal(true);
    }
  };

  // Handler for closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
  };

  //Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "danger">(
    "danger"
  );
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const validateForm = () => {
    const invalidFields: string[] = [];

    // Validate fields in common attribute groups
    productAttributes.CommonAttributes.AttributeGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        const excludedAttributes = ["category_ids", "sku"];
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code] &&
            !excludedAttributes.includes(attribute.attribute_code)
          ) {
            invalidFields.push(attribute.attribute_code);
          }
        });
      }
    });

    // Validate fields in ProductTabs
    allInTabGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code]
          ) {
            invalidFields.push(attribute.attribute_code);
          }
        });
      }
    });
    console.log("invalidFields", invalidFields);
    return invalidFields;
  };

  const handleInputChange = useCallback(
    debounce(async (query: string, attribute: Attribute) => {
      if (attribute.options_getter) {
        setIsLoadingOptions(true);
        const options = await fetchOptions(attribute.options_getter, query);
        setDynamicOptions((prev) => ({
          ...prev,
          [attribute.attribute_code]: options,
        }));
        setIsLoadingOptions(false);
      }
    }, 500),
    []
  );

  const fetchFeatures = async (categoryId: string) => {
    try {
      if (!categoryId) {
        console.error("Category ID is required to fetch features.");
        return [];
      }

      // Use Apollo Client to fetch data
      const { data } = await client.query({
        query: GET_FEATURES_QUERY,
        variables: { categoryId },
      });

      if (data?.getFeatures) {
        return data.getFeatures;
      }

      console.error("No features found for the given category ID.");
      return [];
    } catch (error) {
      console.error("Error fetching features:", error);
      return [];
    }
  };

  const { loading: queryLoading, error, data, refetch } = useQuery(GET_PRODUCT_DETAILS, {
    variables: { productId: Number(productId) },
  });
  // Mapping Livraison_gratuite values
  const livraisonMap: Record<string, number> = {
    "Oui": 17952,  // Yes
    "Non": 17953   // No
  };

  // Mapping Politique_de_retour values
  const politiqueMap: Record<string, number> = {
    "Non éligible": 17956, // Non
    "14 jours": 17955,     // 14j
    "30 jours": 17954      // 30j
  };

  // Add the updateProduct mutation
  const [updateProduct, { loading: updateLoading }] = useMutation(UPDATE_PRODUCT);

  // Handle toggling edit mode
  const handleToggleEditMode = () => {
    if (isReadOnly) {
      // Entering edit mode - save original values for comparison later
      setOriginalValues({ ...formValues });
      setModifiedFields(new Set());
      setIsReadOnly(false);
    } else {
      // Exiting edit mode without saving - revert to original values
      setFormValues(originalValues);
      setModifiedFields(new Set());
      setIsReadOnly(true);
    }
  };

  // Track deleted image IDs
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);

  // Define an extended FileAttachment type that includes the properties we need
  interface ExtendedFileAttachment extends FileAttachment {
    id?: number;
    label?: string;
    originalUrl?: string;
  }
  
  // Helper function to format error messages
  const formatErrorMessage = (message: string): string => {
    // Check for type errors in the input
    if (message.includes('Expected type String') && message.includes('brand')) {
      return "The brand field must be a text value. Please select a valid brand.";
    }
    
    if (message.includes('Expected type Float') && message.includes('price')) {
      return "The price field must be a number. Please enter a valid price.";
    }
    
    // Check for validation errors
    if (message.includes('Variable "$input"')) {
      // Extract the field name from the error message
      const fieldMatch = message.match(/at value\.productData\.([a-zA-Z_]+)/);
      if (fieldMatch && fieldMatch[1]) {
        const fieldName = fieldMatch[1]
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        return `The ${fieldName} field has an invalid value. Please check and try again.`;
      }
    }
    
    // If we can't parse it better, at least make it more user-friendly
    return message
      .replace(/Variable "\$input" got invalid value/g, "Invalid input:")
      .replace(/Expected type [A-Za-z]+ at value\.productData\./g, "Invalid value for ")
      .replace(/;/g, ".")
      .replace(/\./g, ". ");
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    setLoading(true);

    // Check if there are any changes
    if (modifiedFields.size === 0) {
      setToastMessage("No changes detected.");
      setToastVariant("danger");
      setShowToast(true);
      setLoading(false);
      return;
    }

    try {
      // Create a clean productData object with correct field names and types
      const productData: any = {};

      // Debug: Log original and current gallery
      console.log("Original gallery:", originalValues.gallery);
      console.log("Current gallery:", formValues.gallery);
      console.log("Deleted image IDs:", deletedImageIds);

      // Field name mapping from frontend to GraphQL schema
      const fieldNameMapping: Record<string, string> = {
        'Nom_du_modele': 'model_name',
        'Numero_du_modele': 'model_number',
        'reference': 'seller_reference',
        'garantie': 'warranty'
      };

      // Fields to skip (handled separately)
      const skipFields = ['special_to_date', 'special_from_date'];

      // Check if any special price related fields are modified
      const specialPriceFields = ['special_price', 'special_from_date', 'special_to_date'];
      const hasSpecialPriceChanges = specialPriceFields.some(field => modifiedFields.has(field));

      if (hasSpecialPriceChanges) {
        // Format dates correctly for the API
        const fromDate = formValues.special_from_date
          ? new Date(formValues.special_from_date).toISOString().split("T")[0]
          : "";
        const endDate = formValues.special_to_date
          ? new Date(formValues.special_to_date).toISOString().split("T")[0]
          : "";

        console.log("Formatted from_date:", fromDate);
        console.log("Formatted end_date:", endDate);

        // Add special_price as a nested object according to the GraphQL schema
        productData.special_price = {
          value: parseFloat(formValues.special_price) || 0,
          from_date: fromDate,
          end_date: endDate,
        };

        console.log("Special price object being sent to API:", productData.special_price);
      }

      // Handle gallery if it's modified
      if (modifiedFields.has('gallery')) {
        console.log("Using tracked deleted image IDs:", deletedImageIds);
        console.log("Gallery images before processing:", formValues.gallery);

        // Format gallery correctly according to the GraphQL schema
        const galleryData: any = {};

        // Only include images_to_delete if there are actually images to delete
        if (deletedImageIds.length > 0) {
          galleryData.images_to_delete = deletedImageIds;
        }

        // Add new images if there are any
        const newImages = formValues.gallery
          .filter((img: any) => !img.id && img.originalUrl) // Only new images with originalUrl
          .map((img: any) => {
            console.log("Processing image:", img);
            return {
              preview: img.originalUrl || '', // Use the original URL from the API
              roles: img.roles
                ? Object.keys(img.roles).filter(role => img.roles[role] === true)
                : ["image"]
            };
          });

        if (newImages.length > 0) {
          galleryData.images_to_add = newImages;
        }

        // Only add gallery to productData if there's something to update
        if (Object.keys(galleryData).length > 0) {
          productData.gallery = galleryData;
        }

        console.log("Gallery data for API:", JSON.stringify(productData.gallery, null, 2));
      }

      // Handle Livraison_gratuite if it's modified
      if (modifiedFields.has('Livraison_gratuite')) {
        if (formValues.Livraison_gratuite && formValues.Livraison_gratuite.option_id) {
          productData.Livraison_gratuite = parseInt(formValues.Livraison_gratuite.option_id, 10);
          console.log("Formatted Livraison_gratuite:", productData.Livraison_gratuite);
        }
      }

      // Handle politique_de_retour if it's modified
      if (modifiedFields.has('politique_de_retour')) {
        if (formValues.politique_de_retour && formValues.politique_de_retour.option_id) {
          productData.politique_de_retour = parseInt(formValues.politique_de_retour.option_id, 10);
          console.log("Formatted politique_de_retour:", productData.politique_de_retour);
        }
      }

      // Handle garantie if it's modified
      if (modifiedFields.has('garantie')) {
        if (formValues.garantie && formValues.garantie.option_id) {
          productData.warranty = parseInt(formValues.garantie.option_id, 10);
          console.log("Mapped garantie to warranty:", productData.warranty);
        }
      }

      // Handle warranty if it's modified
      if (modifiedFields.has('warranty')) {
        if (formValues.warranty && formValues.warranty.option_id) {
          productData.warranty = parseInt(formValues.warranty.option_id, 10);
          console.log("Formatted warranty:", productData.warranty);
        }
      }

      // Handle price if it's modified
      if (modifiedFields.has('price')) {
        productData.price = parseFloat(formValues.price) || 0;
        console.log("Formatted price as number:", productData.price);
      }

      // Handle attributes - collect all modified attributes from characteristicsAttributes
      const modifiedAttributes: { code: string; value: string | number }[] = [];
      
      // First, handle the attributes array if it's directly modified
      if (modifiedFields.has('attributes')) {
        formValues.attributes.forEach((attr: any) => {
          // Format the value based on its type
          let value = attr.value;
          if (value && typeof value === 'object') {
            if (Array.isArray(value)) {
              // For multi-select, join the values with comma
              value = value.map((item: any) => 
                typeof item === 'object' && item !== null ? 
                  (item.option_id || item.value || '') : 
                  item
              ).join(',');
            } else {
              // For dropdowns, extract option_id or value
              value = value.option_id || value.value || '';
            }
          }
          
          modifiedAttributes.push({
            code: attr.code,
            value: value
          });
        });
      }
      
      // Next, look for individual attribute changes from characteristicsAttributes
      characteristicsAttributes.forEach((attr: Attribute) => {
        if (modifiedFields.has(attr.attribute_code)) {
          // Get the value and normalize it to the correct format
          let value = formValues[attr.attribute_code];
          
          // Format the value based on its type
          if (value && typeof value === 'object') {
            if (Array.isArray(value)) {
              // For multi-select, join the values with comma
              value = value.map((item: any) => 
                typeof item === 'object' && item !== null ? 
                  (item.option_id || item.value || '') : 
                  item
              ).join(',');
            } else {
              // For dropdowns, extract option_id or value
              value = value.option_id || value.value || '';
            }
          }
          
          modifiedAttributes.push({
            code: attr.attribute_code,
            value: value
          });
        }
      });
      
      // If we have any modified attributes, add them to the product data
      if (modifiedAttributes.length > 0) {
        productData.attributes = modifiedAttributes;
        console.log("Modified attributes:", modifiedAttributes);
      }

      // Handle brand field if it's modified
      if (modifiedFields.has('brand')) {
        console.log("original brand:", formValues.brand);
        if (formValues.brand && Array.isArray(formValues.brand) && formValues.brand.length > 0) {
          // Extract the brand value from the selected brand object
          productData.brand = formValues.brand[0].value || "";
          console.log("Formatted brand:", productData.brand);
        } else if (formValues.brand && typeof formValues.brand === 'string') {
          productData.brand = formValues.brand;
        } else {
          // Include the brand field even if it's invalid
          productData.brand = formValues.brand || "";
          console.log("Including potentially invalid brand:", productData.brand);
        }
      }

      // Handle all other modified fields
      modifiedFields.forEach(field => {
        // Skip fields that are handled separately
        if (field === 'gallery' ||
          field === 'attributes' ||
          field === 'Livraison_gratuite' ||
          field === 'politique_de_retour' ||
          field === 'garantie' ||
          field === 'warranty' ||
          field === 'price' ||
          field === 'brand' ||
          field === 'description' ||  // Skip description as it's handled separately
          field === 'short_description' ||  // Skip short_description as it's handled separately
          field === 'name' ||  // Skip name as it's handled separately
          skipFields.includes(field)) {
          return;
        }

        // Map field names according to the GraphQL schema
        if (fieldNameMapping[field]) {
          productData[fieldNameMapping[field]] = formValues[field];
          console.log(`Mapped ${field} to ${fieldNameMapping[field]}:`, productData[fieldNameMapping[field]]);
        } else {
          // Regular fields
          productData[field] = formValues[field];
        }
      });

      // Only proceed if there are changes
      if (modifiedFields.size === 0) {
        setToastMessage("No changes detected.");
        setToastVariant("danger");
        setShowToast(true);
        setLoading(false);
        return;
      }

      console.log("Sending update with modified fields:", productData);

      // Create a clean copy of the data to send to the server
      const cleanProductData: any = {};

      // Map field names and convert types - only include fields that were actually modified
      if (modifiedFields.has('name')) {
        cleanProductData.name = formValues.name; // Use formValues directly for name
        console.log("Added name to cleanProductData:", cleanProductData.name);
      }
      
      if (modifiedFields.has('description')) {
        cleanProductData.description = formValues.description;
        console.log("Added description to cleanProductData:", cleanProductData.description);
      }
      
      if (modifiedFields.has('short_description')) {
        cleanProductData.short_description = formValues.short_description;
        console.log("Added short_description to cleanProductData:", cleanProductData.short_description);
      }

      // Always include these fields from formValues if they're modified
      if (modifiedFields.has('Nom_du_modele')) {
        cleanProductData.model_name = formValues.Nom_du_modele;
      }
      if (modifiedFields.has('Numero_du_modele')) {
        cleanProductData.model_number = formValues.Numero_du_modele;
      }
      if (modifiedFields.has('reference')) {
        cleanProductData.seller_reference = formValues.reference;
      }

      // Only include these fields if they were explicitly modified
      if (modifiedFields.has('Nom_du_modele') && productData.Nom_du_modele) cleanProductData.model_name = productData.Nom_du_modele;
      if (modifiedFields.has('Numero_du_modele') && productData.Numero_du_modele) cleanProductData.model_number = productData.Numero_du_modele;
      if (modifiedFields.has('reference') && productData.reference) cleanProductData.seller_reference = productData.reference;

      // Always include these dropdown fields if they're modified
      if (modifiedFields.has('Livraison_gratuite')) {
        if (formValues.Livraison_gratuite && formValues.Livraison_gratuite.option_id) {
          cleanProductData.Livraison_gratuite = parseInt(formValues.Livraison_gratuite.option_id, 10);
          console.log("Added Livraison_gratuite to cleanProductData:", cleanProductData.Livraison_gratuite);
        }
      }

      if (modifiedFields.has('politique_de_retour')) {
        if (formValues.politique_de_retour && formValues.politique_de_retour.option_id) {
          cleanProductData.politique_de_retour = parseInt(formValues.politique_de_retour.option_id, 10);
          console.log("Added politique_de_retour to cleanProductData:", cleanProductData.politique_de_retour);
        }
      }

      if (modifiedFields.has('garantie')) {
        if (formValues.garantie && formValues.garantie.option_id) {
          cleanProductData.warranty = parseInt(formValues.garantie.option_id, 10);
          console.log("Added warranty to cleanProductData:", cleanProductData.warranty);
        }
      }

      // Handle quantity if it's modified
      if (modifiedFields.has('quantity_and_stock_status')) {
        cleanProductData.quantity = parseInt(formValues.quantity_and_stock_status, 10) || 0;
        console.log("Added quantity to cleanProductData:", cleanProductData.quantity);
      }

      // Handle price - always include it if it's modified, even if it's invalid
      if (modifiedFields.has('price')) {
        // Try to parse as float, but if it fails, include the raw value for server validation
        try {
          cleanProductData.price = parseFloat(formValues.price) || 0;
        } catch (e) {
          cleanProductData.price = formValues.price;
        }
        console.log("Added price to cleanProductData:", cleanProductData.price);
      }

  // Handle special price - must be an object with value, from_date, and end_date
  if (hasSpecialPriceChanges) {
    // When user clears the special price field, don't change the price value, only update dates
    // Get the special price value to preserve - first from original values, fallback to current product data
    const emptySpecialPrice = formValues.special_price === "" || formValues.special_price === null || formValues.special_price === undefined;
    const originalSpecialPrice = originalValues.special_price;
    
    // Retrieve the current special price value, ensuring we keep it even if the field is cleared
    let specialPriceValue = emptySpecialPrice && modifiedFields.has('special_price') 
      ? originalSpecialPrice 
      : formValues.special_price;
    
    // Make sure we have a valid numeric value
    if (specialPriceValue === "" || specialPriceValue === undefined || specialPriceValue === null) {
      // If no value is available in originalValues, use 0 as last resort
      specialPriceValue = 0;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset hours to start of day for comparison
    const todayString = today.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    
    // Get current from_date
    let fromDate = "";
    if (formValues.special_from_date) {
      const fromDateObj = new Date(formValues.special_from_date);
      fromDateObj.setHours(0, 0, 0, 0); // Reset hours for comparison
      fromDate = fromDateObj.toISOString().split("T")[0];
      
      // If user cleared special price (but we're preserving the value)
      if (emptySpecialPrice && modifiedFields.has('special_price')) {
        // Check if from_date is less than today
        if (fromDateObj < today) {
          // Keep from_date, only update end_date to today
          cleanProductData.special_price = {
            value: parseFloat(String(specialPriceValue)),
            from_date: fromDate,
            end_date: todayString
          };
          console.log("Special price dates modified - from_date is before today, setting end_date to today:", cleanProductData.special_price);
        } else {
          // from_date is greater than or equal to today, set both dates to today
          cleanProductData.special_price = {
            value: parseFloat(String(specialPriceValue)),
            from_date: todayString,
            end_date: todayString
          };
          console.log("Special price dates modified - from_date is after today, setting both dates to today:", cleanProductData.special_price);
        }
      } else {
        // Normal update - not clearing the special price
        const endDate = formValues.special_to_date
          ? new Date(formValues.special_to_date).toISOString().split("T")[0]
          : "";
          
        cleanProductData.special_price = {
          value: parseFloat(String(specialPriceValue)),
          from_date: fromDate,
          end_date: endDate
        };
        console.log("Normal special price update with existing dates:", cleanProductData.special_price);
      }
    } else if (emptySpecialPrice && modifiedFields.has('special_price')) {
      // No from_date but special price is being cleared, set both dates to today
      cleanProductData.special_price = {
        value: parseFloat(String(specialPriceValue)),
        from_date: todayString,
        end_date: todayString
      };
      console.log("Special price dates modified - no from_date, setting both dates to today:", cleanProductData.special_price);
    } else {
      // Standard case - create new special price with dates
      const fromDate = formValues.special_from_date
        ? new Date(formValues.special_from_date).toISOString().split("T")[0]
        : todayString; // Use today if no from_date
        
      const endDate = formValues.special_to_date
        ? new Date(formValues.special_to_date).toISOString().split("T")[0]
        : todayString; // Use today if no end_date

      cleanProductData.special_price = {
        value: parseFloat(String(specialPriceValue)) || 0,
        from_date: fromDate,
        end_date: endDate
      };
    }
    
    console.log("Special price being sent:", cleanProductData.special_price);
  }

      // Handle gallery
      if (productData.gallery) cleanProductData.gallery = productData.gallery;
      
      // Include attributes if there are any modified
      if (productData.attributes && productData.attributes.length > 0) {
        cleanProductData.attributes = productData.attributes;
        console.log("Added attributes to cleanProductData:", cleanProductData.attributes);
      }

      // Add brand to cleanProductData if it's modified - always include it even if invalid
      if (modifiedFields.has('brand')) {
        // Brand must be a string, not an array or other type
        if (formValues.brand && Array.isArray(formValues.brand) && formValues.brand.length > 0) {
          // Extract string value from the brand object
          cleanProductData.brand = formValues.brand[0].option_id || formValues.brand[0].value || "";
          console.log("Added brand to cleanProductData:", cleanProductData.brand);
        } else if (formValues.brand && typeof formValues.brand === 'string') {
          cleanProductData.brand = formValues.brand;
        } else {
          // Convert to empty string to avoid type errors
          cleanProductData.brand = "";
          console.log("Added empty string for brand to cleanProductData");
        }
      }
      
      console.log("Clean product data:", cleanProductData);

      const { data } = await updateProduct({
        variables: {
          input: {
            productId: productId,
            productData: cleanProductData
          }
        }
      });

      if (data?.updateProduct?.success) {
        setToastMessage(data.updateProduct.message || "Product updated successfully");
        setToastVariant("success");
        setIsReadOnly(true); // Return to read-only mode
        setModifiedFields(new Set()); // Clear modified fields
        
        // Refresh product data to get the latest changes
        refetch();
      } else {
        // Check if there's a debug message in the response
        const debugMessage = data?.updateProduct?.debugMessage;
        let userFriendlyMessage = data?.updateProduct?.message || "Failed to update product";
        
        // Format the debug message if it exists
        if (debugMessage) {
          // Extract the field name and error message
          const match = debugMessage.match(/([a-zA-Z_]+)\s*:\s*(.*)/);
          if (match) {
            const [, field, message] = match;
            // Convert field_name to Field name format
            const formattedField = field
              .split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            userFriendlyMessage = message.trim();
            // If the message starts with "The", we already have a good format
            if (!userFriendlyMessage.startsWith("The")) {
              userFriendlyMessage = `The ${formattedField.toLowerCase()} ${userFriendlyMessage}`;
            }
          } else {
            // If we can't parse it, just use the debug message directly
            userFriendlyMessage = debugMessage;
          }
        }
        
        setToastMessage(userFriendlyMessage);
        setToastVariant("danger");
      }
    } catch (error: any) {
      console.error("Error updating product:", error);
      
      
      // Try to extract debug message from error response
      let errorMessage = "Error updating product. Please try again.";
      
      // Type guard to check if error has graphQLErrors property
      if (error && typeof error === 'object' && 'graphQLErrors' in error && 
          Array.isArray(error.graphQLErrors) && error.graphQLErrors.length > 0) {
        
        const graphQLError = error.graphQLErrors[0];
        if (graphQLError && typeof graphQLError === 'object' && 'debugMessage' in graphQLError) {
          // Extract and format the debug message
          const debugMessage = graphQLError.debugMessage as string;
          const match = debugMessage.match(/([a-zA-Z_]+)\s*:\s*(.*)/);
          
          if (match) {
            const [, field, message] = match;
            // Convert field_name to Field name format
            const formattedField = field
              .split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            errorMessage = message.trim();
            // If the message starts with "The", we already have a good format
            if (!errorMessage.startsWith("The")) {
              errorMessage = `The ${formattedField.toLowerCase()} ${errorMessage}`;
            }
          } else {
            // If we can't parse it, just use the debug message directly
            errorMessage = formatErrorMessage(debugMessage);
          }
        } else if (graphQLError && typeof graphQLError === 'object' && 'message' in graphQLError) {
          errorMessage = formatErrorMessage(graphQLError.message as string);
        }
      } else if (error && typeof error === 'object' && 'networkError' in error && 
                error.networkError && typeof error.networkError === 'object' && 
                'result' in error.networkError && error.networkError.result && 
                typeof error.networkError.result === 'object' && 
                'errors' in error.networkError.result && 
                Array.isArray(error.networkError.result.errors) && 
                error.networkError.result.errors.length > 0) {
        
        const networkError = error.networkError.result.errors[0];
        if (networkError && typeof networkError === 'object') {
          if ('debugMessage' in networkError) {
            errorMessage = formatErrorMessage(networkError.debugMessage as string);
          } else if ('message' in networkError) {
            errorMessage = formatErrorMessage(networkError.message as string);
          }
        }
      }
      
      setToastMessage(errorMessage);
      setToastVariant("danger");
    } finally {
      setShowToast(true);
      setLoading(false);
    }
  };

  const handleChange = (attribute_code: string, value: any) => {
    console.log(`Updating ${attribute_code} with value:`, value);

    // Track modified fields when in edit mode
    if (!isReadOnly) {
      // Check if the value is actually different from the original
      const originalValue = originalValues[attribute_code];
      const isChanged = JSON.stringify(originalValue) !== JSON.stringify(value);

      // Update the set of modified fields
      const newModifiedFields = new Set(modifiedFields);
      if (isChanged) {
        newModifiedFields.add(attribute_code);
      } else {
        newModifiedFields.delete(attribute_code);
      }
      setModifiedFields(newModifiedFields);
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [attribute_code]: value,
    }));
  };
  const handleCreateVariants = (variants: any[], selectedAttrs: Attribute[]) => {
    console.log("🛠 Variants BEFORE Fixing:", JSON.stringify(variants, null, 2));

    const fixedVariants = variants.map((variant) => ({
      ...variant,
      quantity: variant.quantity ? parseInt(variant.quantity, 10) : 1, // ✅ Default to 1 if missing
      price: variant.price ? parseFloat(variant.price) : 0, // ✅ Ensure it's numeric
    }));

    console.log("✅ Fixed Variants:", JSON.stringify(fixedVariants, null, 2));

    setGeneratedVariants(fixedVariants);
    setSelectedAttributes(selectedAttrs);

    // ✅ Save to form values correctly
    setFormValues((prev) => ({
      ...prev,
      variantsData: fixedVariants,
      selectedAttributes: selectedAttrs,
    }));
  };

  const handleRemoveVariant = (index: number) => {
    const updatedVariants = [...generatedVariants];
    updatedVariants.splice(index, 1);
    setGeneratedVariants(updatedVariants);
    const updatedFormValues = { ...formValues };
    delete updatedFormValues[`selectedAttributeOptions_${index}`];
    delete updatedFormValues[`variantAttribute_${index}`];
    if (index === 0 && formValues.variantAttribute_1) {
      updatedFormValues.variantAttribute_0 = formValues.variantAttribute_1;
      delete updatedFormValues.variantAttribute_1;
      updatedFormValues.selectedAttributeOptions_0 =
        formValues.selectedAttributeOptions_1;
      delete updatedFormValues.selectedAttributeOptions_1;
    } else if (index === 1) {
      delete updatedFormValues.variantAttribute_1;
      delete updatedFormValues.selectedAttributeOptions_1;
    }
    setFormValues({
      ...updatedFormValues,
      variantsData: updatedVariants, // Save updated variantsData
    });
  };
  // ProductVariantsTable.tsx
  const createCompositeKey = (variant: any, attributes: Attribute[]) => {
    return attributes
      .map((attr) => variant[attr.attribute_code]?.option_id || "")
      .join("-");
  };
  const handleDeleteVariant = (compositeKey: string) => {
    setGeneratedVariants((prevVariants) =>
      prevVariants.filter((variant) => {
        const variantKey = createCompositeKey(variant, selectedAttributes);
        return variantKey !== compositeKey;
      })
    );
  };
  const handleUpdateVariantImage = (
    imagedAttributeCode: string,
    imagedAttributeValue: string,
    newImage: FileAttachment
  ) => {
    setGeneratedVariants((prevVariants) => {
      const updatedVariants = prevVariants.map((variant) => {
        if (variant[imagedAttributeCode]?.value === imagedAttributeValue) {
          return {
            ...variant,
            image: newImage,
          };
        }
        return variant;
      });
      setFormValues((prevValues) => ({
        ...prevValues,
        variantsData: updatedVariants, // Save updated variantsData
      }));
      return updatedVariants;
    });
  };
  const handleUpdateVariant = (updatedVariants: any[]) => {
    setGeneratedVariants(updatedVariants);

    setFormValues((prevValues) => ({
      ...prevValues,
      variantsData: updatedVariants,
    }));
  };
  const handleFileDrop = async (files: any[]) => {
    const updatedGallery = [...(formValues["gallery"] || [])];
    files.forEach((file) => {
      updatedGallery.push(file);
      setFormValues((prevValues) => ({
        ...prevValues,
        gallery: updatedGallery,
      }));
    });

    // Track gallery changes
    if (!isReadOnly) {
      const newModifiedFields = new Set(modifiedFields);
      newModifiedFields.add('gallery');
      setModifiedFields(newModifiedFields);
    }
  };

  const handleFileRemove = (fileName: string, index?: number) => {
    console.log(`Removing file with name: ${fileName}, index: ${index}`);

    setFormValues((prevValues) => {
      console.log("Current gallery:", prevValues["gallery"]);

      // If index is provided directly, use it
      let imageToRemove;

      if (index !== undefined && index >= 0 && index < prevValues["gallery"].length) {
        // Use the provided index directly
        imageToRemove = prevValues["gallery"][index];
        console.log(`Using provided index ${index} to find image:`, imageToRemove);
      } else {
        // Try to find by name or label as fallback
        console.log("Gallery item names:", prevValues["gallery"].map((file: any) => file.name || (file as any).label));
        imageToRemove = prevValues["gallery"].find(
          (file: any) => file.name === fileName || (file as any).label === fileName
        );
        console.log("Image to remove (found by name):", imageToRemove);

        // If still not found, try to parse fileName as an index
        if (!imageToRemove) {
          const parsedIndex = parseInt(fileName, 10);
          if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < prevValues["gallery"].length) {
            imageToRemove = prevValues["gallery"][parsedIndex];
            console.log(`Found image by parsed index ${parsedIndex}:`, imageToRemove);
          }
        }
      }

      // Log all image IDs for debugging
      console.log("All image IDs:", prevValues["gallery"].map((img: any, idx: number) =>
        `Index ${idx}: ID ${(img as any).id || 'none'}`
      ));

      // If the image has an ID, add it to the deletedImageIds array
      if (imageToRemove && (imageToRemove as any).id) {
        console.log(`Adding image ID ${(imageToRemove as any).id} to deletedImageIds`);
        setDeletedImageIds(prev => [...prev, (imageToRemove as any).id]);
      } else {
        console.log("No ID found for the image to remove");
      }

      // Remove the image from the gallery
      let updatedGallery;
      if (imageToRemove) {
        updatedGallery = prevValues["gallery"].filter(
          (file: any) => file !== imageToRemove
        );
      } else {
        // Fallback: try to filter by name
        updatedGallery = prevValues["gallery"].filter(
          (file: any) => file.name !== fileName && (file as any).label !== fileName
        );
      }

      console.log("Updated gallery:", updatedGallery);

      // Track gallery changes
      if (!isReadOnly) {
        const newModifiedFields = new Set(modifiedFields);
        newModifiedFields.add('gallery');
        setModifiedFields(newModifiedFields);
      }

      return {
        ...prevValues,
        gallery: updatedGallery,
      };
    });
  };

  const handleFileUpdate = (
    index: number,
    updatedData: Partial<FileAttachment>
  ) => {
    setFormValues((prevValues) => {
      const updatedGallery = [...(prevValues["gallery"] || [])];

      // Clear the roles from all other images
      if (updatedData.roles) {
        Object.keys(updatedData.roles).forEach((role) => {
          if (updatedData.roles![role]) {
            updatedGallery.forEach((file, i) => {
              if (i !== index && file.roles && file.roles![role]) {
                file.roles![role] = false;
              }
            });
          }
        });
      }

      updatedGallery[index] = {
        ...updatedGallery[index],
        ...updatedData,
      };

      // Track gallery changes
      if (!isReadOnly) {
        const newModifiedFields = new Set(modifiedFields);
        newModifiedFields.add('gallery');
        setModifiedFields(newModifiedFields);
      }

      return {
        ...prevValues,
        gallery: updatedGallery,
      };
    });
  };

  const handleFileIndexChange = (fromIndex: number, toIndex: number) => {
    setFormValues((prevValues) => {
      const updatedGallery = [...(prevValues["gallery"] || [])];
      const [movedFile] = updatedGallery.splice(fromIndex, 1);
      updatedGallery.splice(toIndex, 0, movedFile);

      // Track gallery changes
      if (!isReadOnly) {
        const newModifiedFields = new Set(modifiedFields);
        newModifiedFields.add('gallery');
        setModifiedFields(newModifiedFields);
      }

      return {
        ...prevValues,
        gallery: updatedGallery,
      };
    });
  };
  // Function to get swatch color from features
  const getSwatchColor = (optionId: string, features: Attribute[]): string | undefined => {
    const colorFeature = features.find((attr) => attr.attribute_code === "couleur");
    if (!colorFeature || !colorFeature.options) return undefined;

    const option = colorFeature.options.find((opt) => String(opt.option_id) === String(optionId));
    return option?.swatch || undefined;
  };

  // Fetch brand options dynamically when component mounts
  useEffect(() => {
    const loadBrandOptions = async () => {
      try {
        setIsLoadingOptions(true);
        const options = await fetchAttributeOptions("brand");
        setBrandOptions(options);
      } catch (error) {
        console.error("Error fetching brand options:", error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadBrandOptions();
  }, []);

  useEffect(() => {
    if (data?.sellerProductDetails) {
      const product = data.sellerProductDetails;
      const categories = product.categories || [];
      // Find the matching brand option from `brandOptions`
      const brandOption = brandOptions.find(
        (option) => option.value === product.brand
      );

      // ✅ Extract category levels
      const level2 = categories.find((c: Category) => Number(c.level) === 2);
      const level3 = categories.find((c: Category) => Number(c.level) === 3);
      const level4 = categories.find((c: Category) => Number(c.level) === 4);
      const lastSelectedCategory = level4?.id || level3?.id || level2?.id || null;
      // Log the custom attributes to see what's available
      console.log("Custom Attributes:", product.custom_attributes);

      // Process custom attributes with special handling for multiselect attributes
      const customAttributes = product.custom_attributes?.reduce(
        (acc: Record<string, any>, attr: { code: string; content: string; value: string; label: string; __typename?: string }) => {
          console.log(`Processing attribute ${attr.code}:`, attr);
          
          // Find the attribute definition to check if it's a multiselect
          const attributeDef = characteristicsAttributes.find(
            (a: Attribute) => a.attribute_code === attr.code
          );
          
          if (attributeDef && attributeDef.input_type === 'multiselect' && attr.value) {
            // For multiselect attributes, convert comma-separated values to array of option objects
            const valueIds = attr.value.split(',');
            const valueLabels = attr.content ? attr.content.split(',') : [];
            
            // Create an array of option objects for Typeahead
            const options = valueIds.map((id, index) => ({
              option_id: id,
              value: valueLabels[index] || id,
              __typename: 'Option'
            }));
            
            acc[attr.code] = options;
            console.log(`Converted multiselect attribute ${attr.code} to:`, options);
          } else {
            // Standard attributes - use the value field
            acc[attr.code] = attr.value || attr.content || "";
          }

          return acc;
        },
        {} as Record<string, any>
      );

      console.log("Processed Custom Attributes:", customAttributes);
      // ✅ Extract attributes from API response
      const extractedAttributeCodes = new Set<string>();

      // ✅ Map Variants to the Expected Format
      const variantsData = product.variants.map((variant: Variant) => {
        variant.attributes.forEach((attr) => extractedAttributeCodes.add(attr.code));

        return {
          id: variant.id,
          ...variant.attributes.reduce<Record<string, any>>((acc, attr) => {
            const swatchColor =
              attr.code === "couleur"
                ? getSwatchColor(attr.value, characteristicsAttributes) // ✅ Wait for characteristicsAttributes
                : undefined;

            acc[attr.code] = {
              option_id: attr.value,
              value: attr.content || attr.value,
              ...(swatchColor ? { swatch: swatchColor } : {}),
            };

            return acc;
          }, {}),
          quantity: variant.quantity || 0,
          price: variant.price || 0,
          image: variant.image
            ? {
              name: variant.image.label || "Uploaded Image",
              size: 57,
              resolution: "740x674 px",
              preview: variant.image.url,
              originalUrl: variant.image.url,
              uploadProgress: 100,
              isUploading: false,
            }
            : undefined,
        };
      });

      // ✅ Convert extracted attributes to a proper format
      const extractedAttributes: Attribute[] = Array.from(extractedAttributeCodes).map((code) => ({
        attribute_id: code,
        attribute_code: code,
        frontend_label: code, // Placeholder label (should be updated with real labels)
        is_required: "0",
        input_type: "select",
        is_imaged: code === "couleur", // ✅ Detect if attribute is color-based
        options: [],
      }));

      setGeneratedVariants(variantsData);
      setSelectedAttributes([]); // Temporarily clear attributes

      if (lastSelectedCategory) {
        // **Fetch features based on category**
        fetchFeatures(lastSelectedCategory.toString()).then((features) => {
          setCharacteristicsAttributes(features);

          // ✅ Merge extracted attributes with fetched options
          const mergedAttributes = extractedAttributes.map((attr) => {
            const feature = features.find((f: Attribute) => f.attribute_code === attr.attribute_code);
            return {
              ...attr,
              frontend_label: feature?.frontend_label || attr.attribute_code, // Replace default label
              options: feature?.options || [],
            };
          });

          // ✅ Filter out attributes that belong to product variations
          const variationAttributes = productAttributes.VariationAttributes.filter((variationAttr) =>
            features.some((fetchedAttr: Attribute) => fetchedAttr.attribute_code === variationAttr.attribute_code)
          ).map((variationAttr) => {
            const matchedAttr = features.find((fetchedAttr: Attribute) => fetchedAttr.attribute_code === variationAttr.attribute_code);
            return matchedAttr ? { ...variationAttr, options: matchedAttr.options } : variationAttr;
          });

          setSelectedAttributes(mergedAttributes);
          setVariationAttributes(variationAttributes);
        });
      }


      // ✅ Ensure form values are updated correctly
      setFormValues((prev) => ({
        ...prev,
        type_id: product.type_id || prev.type_id || "configurable",
        name: product.name || prev.name || "",
        brand: brandOption ? [brandOption] : [], // ✅ Set as array for Typeahead
        Nom_du_modele: product.model_name || prev.model_name || "",
        Numero_du_modele: product.model_number || prev.model_number || "",
        reference: product.seller_reference || prev.seller_reference || "",
        description: product.description || prev.description || "",
        short_description: product.short_description || prev.short_description || "",
        price: product.price || prev.price || 0,
        special_price: product.special_price?.value != null
          ? product.special_price.value
          : prev.special_price || undefined,

        special_from_date: product.special_price?.value != null && product.special_price?.start_date
          ? new Date(product.special_price.start_date)
          : undefined,

        special_to_date: product.special_price?.value != null && product.special_price?.end_date
          ? new Date(product.special_price.end_date)
          : undefined,

        category: {
          level2: level2?.id || null,
          level2_name: level2?.name || "",
          level3: level3?.id || null,
          level3_name: level3?.name || "",
          level4: level4?.id || null,
          level4_name: level4?.name || "",
        },

        Livraison_gratuite: product.free_shipping
          ? {
            option_id: String(product.free_shipping),
            value: Object.keys(livraisonMap).find((key) => livraisonMap[key] === Number(product.free_shipping)) || "Non"
          }
          : prev.Livraison_gratuite || { option_id: "17953", value: "Non" },

        politique_de_retour: product.return_policy
          ? {
            option_id: String(product.return_policy),
            value: Object.keys(politiqueMap).find((key) => politiqueMap[key] === Number(product.return_policy)) || "Non éligible"
          }
          : prev.politique_de_retour || { option_id: "17956", value: "Non éligible" },

        quantity_and_stock_status: product.quantity !== null ? String(product.quantity) : prev.quantity_and_stock_status || "0",

        mode_expedition: product.shipping_method
          ? {
            option_id: String(product.shipping_method),
            value:
              productAttributes.CommonAttributes.AttributeGroups.find((group) => group.name === "Informations Logistiques")
                ?.attributes.find((attr) => attr.attribute_code === "mode_expedition")
                ?.options?.find((option) => option.option_id === String(product.shipping_method))?.value || "Unknown",
          }
          : prev.mode_expedition || {},

        weight: product.weight || prev.weight || 0,
        longueur25: product.package_width || prev.package_width || 0,
        dimensions_de_l_outil_hauteur1: product.package_height || prev.package_height || 0,
        dimensions_de_l_outil_largeur1: product.package_depth || prev.package_depth || 0,
        garantie: product.warranty
          ? {
            option_id: String(product.warranty),
            value:
              productAttributes.CommonAttributes.AttributeGroups.find((group) => group.name === "Offre")
                ?.attributes.find((attr) => attr.attribute_code === "garantie")
                ?.options?.find((option) => option.option_id === String(product.warranty))?.value || "Non",
          }
          : prev.warranty,

        attributes: Array.isArray(product.attributes)
          ? product.attributes.map((attr: { code: string; value: string }) => ({
            code: attr.code,
            value: attr.value,
          }))
          : prev.attributes || [],

        ...customAttributes,

        gallery: Array.isArray(product.images)
          ? product.images.map((file: { id: number; url: string; label: string; roles?: string[] }) => ({
            id: file.id, // Store the image ID
            preview: file.url || "",
            label: file.label || "",
            roles: file.roles?.length
              ? file.roles.reduce((acc, role) => ({ ...acc, [role]: true }), {})
              : { image: true },
          }))
          : prev.gallery || [],
        // selectedAttributes: extractedAttributes,
        // variantsData,
      }));
      if (lastSelectedCategory) {
        fetchFeatures(lastSelectedCategory.toString()).then((features) => {
          setCharacteristicsAttributes(features);
        });
      }
    }
  }, [data, characteristicsAttributes]);

  // Second useEffect to log updates after the state is set
  useEffect(() => {
    console.log("✅ Updated Generated Variants:", JSON.stringify(generatedVariants, null, 2));
    console.log("✅ Updated Selected Attributes:", JSON.stringify(selectedAttributes, null, 2));
  }, [generatedVariants, selectedAttributes]);
  const commonAttributeGroups =
    productAttributes.CommonAttributes.AttributeGroups.filter(
      (group) => !group.display || group.display !== "inTab"
    );

  const inTabAttributeGroups =
    productAttributes.CommonAttributes.AttributeGroups.filter(
      (group) => group.display && group.display === "inTab"
    );

  const allInTabGroups: AttributeGroup[] = [...inTabAttributeGroups];
  const characteristicsGroup = allInTabGroups.find(
    (group) => group.name === "Caractéristiques"
  );

  if (characteristicsGroup) {
    if (Array.isArray(characteristicsGroup.attributes)) {
      characteristicsGroup.attributes = characteristicsAttributes;
    }
  } else {
    allInTabGroups.push({
      name: "Caractéristiques",
      icon: "sliders",
      attributes: characteristicsAttributes,
    });
  }
  return (
    <div>
      {/* Edit/Save buttons */}
      <div className="mb-5">          
        {/* Status bar spanning full width */}
        <div 
          className={`d-flex align-items-center mb-4 py-3 px-4 w-100 ${
            data?.sellerProductDetails?.status === 1 
              ? 'bg-success bg-opacity-10 border-bottom border-success' 
              : 'bg-danger bg-opacity-10 border-bottom border-danger'
          }`}
          style={{marginLeft: '-12px', marginRight: '-12px', width: 'calc(100% + 24px)'}}
        >
          <div className="d-flex align-items-center">
            {/* Sales Status Badge */}
            <span className={`badge me-2 border ${
              data?.sellerProductDetails?.status === 1 
                ? 'border-success text-success' 
                : 'border-danger text-danger'
            } bg-body`}>
              {queryLoading 
                ? t("loading") 
                : data?.sellerProductDetails?.status === 1 
                  ? t("onSale") 
                  : t("closeForSale")}
            </span>
            
            {/* Approval Status Badge */}
            <span className={`badge border ${
              data?.sellerProductDetails?.product_status === 1 
                ? 'border-success text-success'
                : data?.sellerProductDetails?.product_status === 3 
                  ? 'border-danger text-danger'
                  : 'border-warning text-warning'
            } bg-body`}>
              {queryLoading 
                ? t("loading") 
                : data?.sellerProductDetails?.product_status === 1 
                  ? t("approved") 
                  : data?.sellerProductDetails?.product_status === 3 
                    ? t("rejected") 
                    : t("disapproved")}
            </span>
          </div>
          
          {/* SKU display on right side */}
          {data?.sellerProductDetails?.sku && (
            <div className="text-body-tertiary ms-auto">
              <small className="fw-semibold">{t("sku")}:</small> {data.sellerProductDetails.sku}
            </div>
          )}
        </div>
        
        {/* Title and button row */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Button 
              variant="link" 
              className="p-0 me-3 text-body-tertiary" 
              onClick={() => navigate('/products')}
              aria-label={t("backToProductList")}
            >
              <FontAwesomeIcon icon={faArrowLeft} size="lg" />
            </Button>
            <h2 className="mb-0" style={{ textAlign: isArabic ? "right" : "left" }}>
              {t("productDetails")}
            </h2>
          </div>
          
          <div>
            {isReadOnly ? (
              <Button
                variant="primary"
                onClick={handleToggleEditMode}
              >
                <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
                {t("Update Product")}
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button
                  variant="danger"
                  onClick={handleToggleEditMode}
                >
                  <FontAwesomeIcon icon={faXmark} className="me-2" />
                  {t("Cancel")}
                </Button>
                <Button
                  variant="success"
                  onClick={handleSaveChanges}
                  disabled={modifiedFields.size === 0 || loading}
                >
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  {loading ? t("Saving...") : t("Save Changes")}
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Creation/Update timestamp info */}
        <div 
          className="d-flex align-items-center gap-3 text-body-tertiary mb-3"
          style={{ textAlign: isArabic ? "right" : "left" }}
        >
          {data?.sellerProductDetails?.created_at && (
            <>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faFloppyDisk} className="me-2 text-primary" />
                <span className="fw-semibold">{t("Created")}:</span>
                <span className="ms-2">{new Date(data.sellerProductDetails.created_at).toLocaleDateString()}</span>
              </div>
              <div className="vr" style={{ height: '20px' }}></div>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faPenToSquare} className="me-2 text-success" />
                <span className="fw-semibold">{t("Updated")}:</span>
                <span className="ms-2">{new Date(data.sellerProductDetails.updated_at).toLocaleDateString()}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Toast for success/errors */}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
      >
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          bg={toastVariant === "success" ? "success" : "danger"}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <fieldset disabled={isReadOnly}>

        <form className="mb-12">
          {/* <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2
              className="mb-2"
              style={{ textAlign: isArabic ? "right" : "left" }}
            >
              {t("productDetails")}
            </h2>
            <h5
              className="text-body-tertiary fw-semibold"
              style={{ textAlign: isArabic ? "right" : "left" }}
            >
              {t("viewProductTitle")}
            </h5>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="phoenix-secondary"
              type="button"
              onClick={handleClear}
            >
              {t("clear")}
            </Button>
            <Button
              variant="phoenix-primary"
              type="button"
              onClick={handleSaveDraft}
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              <FontAwesomeIcon
                icon={faFloppyDisk}
                style={{ marginRight: "8px" }}
              />
              {t("saveDraft")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handlePublishProduct}
            >
              {t("publishProduct")}
            </Button>
          </div>
        </div> */}

          <Row className="g-5">
            {/* Left side: common attributes + tabs */}
            <Col xs={12} xl={8}>
              {commonAttributeGroups.map((group) => {
                if (typeof group.attributes === "string") return null;
                return group.attributes.map((attribute: Attribute) => {
                  const inputValue = formValues[attribute.attribute_code] || "";
                  const savedOptions = Array.isArray(
                    formValues[attribute.attribute_code]
                  )
                    ? formValues[attribute.attribute_code]
                    : [];
                  const combinedOptions =
                    attribute.attribute_code === "brand"
                      ? brandOptions // ✅ Use the specific brand options
                      : [
                        { option_id: "0", value: "", swatch: "" },
                        ...(Array.isArray(dynamicOptions[attribute.attribute_code])
                          ? dynamicOptions[attribute.attribute_code]
                          : Array.isArray(attribute.options)
                            ? attribute.options
                            : [])
                      ];
                  switch (attribute.input_type) {
                    case "text":
                    case "price":
                    case "weight":
                      return (
                        <Form.Group key={attribute.attribute_id} className="mb-3">
                          <Form.Label
                            style={{ textAlign: isArabic ? "right" : "left" }}
                          >
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Form.Control
                            type={
                              attribute.input_type === "text" ? "text" : "number"
                            }
                            placeholder={t(attribute.frontend_label)}
                            required={attribute.is_required === "1"}
                            value={inputValue}
                            onChange={(e) =>
                              handleChange(
                                attribute.attribute_code,
                                e.target.value
                              )
                            }
                            className={
                              invalidFields.includes(attribute.attribute_code)
                                ? "is-invalid"
                                : ""
                            }
                          />
                        </Form.Group>
                      );
                    case "textarea":
                      return (
                        <Form.Group key={attribute.attribute_id} className="mb-3">
                          <Form.Label
                            style={{ textAlign: isArabic ? "right" : "left" }}
                          >
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <div className="position-relative">
                            <TinymceEditor
                              key={`${attribute.attribute_code}-${isReadOnly ? 'readonly' : 'editable'}`}
                              options={{
                                height: "15rem",
                                placeholder: t(attribute.frontend_label),
                                readonly: isReadOnly,
                                menubar: !isReadOnly,
                                toolbar: !isReadOnly ? "bold italic underline link bullist numlist" : false,
                                inline: false,
                                setup: (editor) => {
                                  editor.on("init", () => {
                                    if (isReadOnly) {
                                      editor.getBody().setAttribute("contenteditable", "false");
                                    } else {
                                      editor.getBody().setAttribute("contenteditable", "true");
                                      editor.focus();
                                    }
                                  });
                                },
                              }}
                              value={inputValue}
                              onChange={(content) => {
                                // Make sure to track changes for rich text fields
                                if (!isReadOnly) {
                                  const originalValue = originalValues[attribute.attribute_code];
                                  const isChanged = originalValue !== content;

                                  // Update the set of modified fields
                                  const newModifiedFields = new Set(modifiedFields);
                                  if (isChanged) {
                                    newModifiedFields.add(attribute.attribute_code);
                                    setModifiedFields(newModifiedFields);
                                  }
                                }
                                handleChange(attribute.attribute_code, content);
                              }}
                            />
                          </div>
                        </Form.Group>
                      );
                    case "gallery":
                      return (
                        <Form.Group key={attribute.attribute_id} className="mb-3">
                          <Form.Label
                            style={{ textAlign: isArabic ? "right" : "left" }}
                          >
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Dropzone
                            accept={{
                              "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                            }}
                            onDrop={handleFileDrop}
                            defaultFiles={(formValues.gallery || []).map(
                              (f: FileAttachment) => ({
                                name: f.name,
                                preview: f.preview,
                                size: f.size,
                                resolution: f.resolution,
                                altText: f.altText,
                                hideFromProductPage: f.hideFromProductPage,
                                roles: f.roles,
                              })
                            )}
                            onFileRemove={handleFileRemove}
                            onFileUpdate={handleFileUpdate}
                            onFileIndexChange={handleFileIndexChange}
                            productId={Number(productId)}
                          />
                        </Form.Group>
                      );
                    case "cascader":
                      // Add SKU field before categories
                      if (attribute.attribute_code === "category_ids") {
                        return (
                          <React.Fragment key={attribute.attribute_id}>
                            {/* SKU Field */}
                            <Form.Group className="mb-3">
                              <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
                                {t("SKU")}
                              </Form.Label>
                              <div className="border rounded p-2 d-flex align-items-center" 
                                style={{ 
                                  backgroundColor: 'var(--bs-body-bg)',
                                  fontSize: '0.8rem',
                                  color: 'var(--bs-body-color)',
                                  borderColor: 'var(--bs-border-color)'
                                }}>
                                <span>{data?.sellerProductDetails?.sku || ""}</span>
                              </div>
                            </Form.Group>
                            
                            {/* Categories Field */}
                            <Form.Group className="mb-5">
                              <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
                                {t("attributes.categories")} <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="border rounded p-2" 
                                style={{ 
                                  backgroundColor: 'var(--bs-body-bg)',
                                  fontSize: '0.8rem',
                                  color: 'var(--bs-body-color)', 
                                  borderColor: 'var(--bs-border-color)'
                                }}>
                                {[
                                  formValues.category?.level2_name,
                                  formValues.category?.level3_name,
                                  formValues.category?.level4_name,
                                ]
                                  .filter(Boolean) // Remove undefined/null values
                                  .join(" > ")} {/* Join with " > " */}
                              </div>
                            </Form.Group>
                          </React.Fragment>
                        );
                      }
                      
                      return (
                        <Form.Group className="mb-5">
                          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
                            {t("attributes.categories")} <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={[
                              formValues.category?.level2_name,
                              formValues.category?.level3_name,
                              formValues.category?.level4_name,
                            ]
                              .filter(Boolean) // Remove undefined/null values
                              .join(" > ")} // Join with " > "
                            readOnly
                            className="bg-light"
                          />
                        </Form.Group>
                      );
                    case "select":
                      return (
                        <Form.Group key={attribute.attribute_id} className="mb-3">
                          <Form.Label
                            style={{ textAlign: isArabic ? "right" : "left" }}
                          >
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Typeahead
                            id={attribute.attribute_id}
                            className={
                              invalidFields.includes(attribute.attribute_code) ? "is-invalid" : ""
                            }
                            labelKey="value"
                            options={brandOptions} // Use brand options
                            isLoading={isLoadingOptions}
                            placeholder={t(attribute.frontend_label)}
                            onInputChange={(query) => {
                              if (attribute.options_getter) {
                                handleInputChange(query, attribute);
                              }
                            }}
                            selected={formValues.brand || []} // Ensure it's an array
                            onChange={(selected) => {
                              console.log("Brand selected:", selected);
                              handleChange(attribute.attribute_code, selected);
                            }}
                            disabled={isReadOnly} // Explicitly set disabled state
                            renderMenuItemChildren={(option) => {
                              const opt = option as Option;
                              return (
                                <div className="d-flex align-items-center">
                                  {opt.swatch && (
                                    <div
                                      style={{
                                        backgroundColor: opt.swatch,
                                        width: "16px",
                                        height: "16px",
                                        marginRight: "8px",
                                        border: "1px solid #ccc",
                                      }}
                                    />
                                  )}
                                  <span>{t(opt.value || "")}</span>
                                </div>
                              );
                            }}
                          />
                        </Form.Group>
                      );
                    case "multiselect":
                      return (
                        <Form.Group key={attribute.attribute_id} className="mb-3">
                          <Form.Label
                            style={{ textAlign: isArabic ? "right" : "left" }}
                          >
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Typeahead
                            id={attribute.attribute_id}
                            className={
                              invalidFields.includes(attribute.attribute_code)
                                ? "is-invalid"
                                : ""
                            }
                            labelKey="value"
                            multiple={attribute.input_type === "multiselect"}
                            options={combinedOptions}
                            isLoading={isLoadingOptions}
                            placeholder={t(attribute.frontend_label)}
                            onInputChange={(query) => {
                              if (attribute.options_getter) {
                                handleInputChange(query, attribute);
                              }
                            }}
                            // If we have savedOptions already, use them
                            selected={
                              Array.isArray(savedOptions) ? savedOptions : []
                            }
                            onChange={(selected) =>
                              handleChange(attribute.attribute_code, selected)
                            }
                            renderMenuItemChildren={(option) => {
                              const opt = option as Option;
                              return (
                                <div className="d-flex align-items-center">
                                  {opt.swatch && (
                                    <div
                                      style={{
                                        backgroundColor: opt.swatch,
                                        width: "16px",
                                        height: "16px",
                                        marginRight: "8px",
                                        border: "1px solid #ccc",
                                      }}
                                    />
                                  )}
                                  <span>{t(opt.value || "")}</span>
                                </div>
                              );
                            }}
                          />
                        </Form.Group>
                      );
                    default:
                      return null;
                  }
                });
              })}
              <ProductTabs
                AttributeGroups={allInTabGroups as unknown as AttributeGroup[]}
                formValues={formValues}
                handleChange={handleChange}
                invalidFields={invalidFields}
                readOnly={isReadOnly} // ✅ Pass readOnly prop
                // Remove "Caractéristiques" from alwaysReadOnlyTabs to make it editable
                alwaysReadOnlyTabs={[]} 
                alwaysReadOnlyFields={[
                  "mode_expedition", // Shipping Method
                  "weight", // Weight
                  "longueur25", // Package Width
                  "dimensions_de_l_outil_hauteur1", // Package Height
                  "dimensions_de_l_outil_largeur1" // Package Depth
                ]} // Make logistics fields always read-only
              />
            </Col>

            {/* Right side: Variation form card */}
            {/* <Col xs={12} xl={4}>
            <Row className="g-2">
              <Col xs={12} xl={12}>
                <VariantFormCard
                  variationAttributes={variationAttributes}
                  handleChange={handleChange}
                  formValues={formValues}
                  onCreateVariants={handleCreateVariants}
                  onRemoveVariant={handleRemoveVariant}
                  isLoadingAttributes={isLoadingAttributes}
                />
              </Col>
            </Row>
          </Col> */}
            {/* Right side column */}
            <Col xs={12} xl={4}>
              <Row className="g-2">
                <Col xs={12} xl={12}>
                  {/* Deactivation reasons card with modal-inspired UI - only show if deactivation_reason exists */}
                  {data?.sellerProductDetails?.deactivation_reason && (
                    <Card className="h-100 mb-3">
                      <Card.Header className="bg-body-tertiary py-3">
                        <h5 className="mb-0 text-body">
                          <FontAwesomeIcon icon={faFileExport} className="me-2" />
                          {t("Deactivation Reasons")}
                        </h5>
                      </Card.Header>
                      <Card.Body className="p-4">
                        {data?.sellerProductDetails?.deactivation_reason?.reason && 
                         data?.sellerProductDetails?.deactivation_reason?.reason.length > 0 ? (
                          <div>
                            <div className="mb-4">
                              <h5 className="text-body mb-3 border-bottom pb-2">
                                <strong>{t('products.reasonsForDeactivation', 'Reasons for Deactivation')}:</strong>
                              </h5>
                              <div className="mb-4">
                                {data.sellerProductDetails.deactivation_reason.reason.map((reason: string, index: number) => (
                                  <div key={index} className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3" 
                                      style={{ width: '28px', height: '28px', fontSize: '14px' }}>
                                      {index + 1}
                                    </div>
                                    <div className="p-2 bg-body-tertiary rounded w-100">
                                      <span className="fw-semibold">{reason}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {data.sellerProductDetails.deactivation_reason.description && (
                                <div className="mt-4">
                                  <h5 className="text-body mb-3 border-bottom pb-2">
                                    <strong>{t('products.additionalDetails', 'Additional Details')}:</strong>
                                  </h5>
                                  <div className="p-3 bg-body-tertiary rounded border">
                                    <p className="mb-0 text-body">{data.sellerProductDetails.deactivation_reason.description}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="text-muted mb-3">
                              <span className="fa-stack fa-2x">
                                <FontAwesomeIcon icon={faFileExport} className="text-secondary opacity-25 fa-stack-2x" />
                                <FontAwesomeIcon icon={faAngleDown} className="text-secondary fa-stack-1x" />
                              </span>
                            </div>
                            <p className="fs-5 text-body">{t('products.noDeactivationReasons', 'No deactivation reasons available')}</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </form>

        {/* Table of generated variants */}
        <Row className="g-2">
          <Col xs={12} xl={12}>
            {generatedVariants.length > 0 && (
              <ProductVariantsTable
                // 2) Passing the same ref to the child
                tableHeaderRef={tableHeaderRef}
                variantsData={generatedVariants}
                selectedAttributes={selectedAttributes}
                onUpdateVariant={handleUpdateVariant}
                formValues={formValues}
                onUpdateVariantImage={handleUpdateVariantImage}
              />
            )}
          </Col>
        </Row>
      </fieldset>

    </div>
  );
};

export default ProductDetails;
